import { useMemo, useState } from "react";
import { Sprout, Wheat, X, Lock, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { authFetch } from "@/lib/api";
import { toast } from "sonner";
import ModalPortal from "@/components/ui/modal-portal";
import { API_BASE } from "./constants";

export const FIELD_EVENT_TYPES = {
  PLANTATION: { label: "Plantation", color: "#3F7D3A", bg: "#E6F2DF", icon: Sprout, qty: "Quantité plantée",
    units: ["plants", "boutures", "t de boutures", "kg de semences", "plants/ha"] },
  RECOLTE: { label: "Récolte", color: "#9A6A12", bg: "#FBF0D9", icon: Wheat, qty: "Quantité récoltée",
    units: ["t", "kg", "régimes", "caisses", "t/ha"] },
};
export const isFieldEvent = (e) => !!FIELD_EVENT_TYPES[e?.usage_type];

const today = () => new Date().toISOString().slice(0, 10);

/**
 * Plantation / récolte dans le registre (demande terrain) : date, culture, variété, quantité, surface.
 * Enregistrée puis validée et verrouillée comme un traitement ; correction = nouvelle version avec motif.
 * mode : « new », « edit » (brouillon) ou « correct » (entrée verrouillée).
 */
export default function FieldEventForm({ parcelId, ilotId, cultureType, surfaceHa, entries = [], entry, mode = "new", onClose, onDone }) {
  const varieties = useMemo(() => [...new Set(entries.map((x) => x.crop?.variety).filter(Boolean))], [entries]);
  const lastPlanting = useMemo(() => entries
    .filter((x) => x.usage_type === "PLANTATION" && x.status !== "corrected" && (!ilotId || !x.ilot_id || x.ilot_id === ilotId))
    .sort((a, b) => (b.timing?.use_date || "").localeCompare(a.timing?.use_date || ""))[0], [entries, ilotId]);

  const [kind, setKind] = useState(entry?.usage_type || "PLANTATION");
  const [date, setDate] = useState(entry?.timing?.use_date || today());
  const [crop, setCrop] = useState(entry?.crop?.crop_text || lastPlanting?.crop?.crop_text || cultureType || "");
  const [variety, setVariety] = useState(entry?.crop?.variety || lastPlanting?.crop?.variety || "");
  const [qty, setQty] = useState(entry?.cultivation?.quantity_value ?? "");
  const [unit, setUnit] = useState(entry?.cultivation?.quantity_unit || FIELD_EVENT_TYPES[entry?.usage_type || "PLANTATION"].units[0]);
  const [surface, setSurface] = useState(entry?.treated_unit?.value ?? (surfaceHa ? Number(surfaceHa) : ""));
  const [notes, setNotes] = useState(entry?.recommended_extra_data?.observations || "");
  const [reason, setReason] = useState("");
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState([]);
  // Brouillon créé par un premier essai refusé : les essais suivants le modifient (pas de doublon)
  const [draftId, setDraftId] = useState(null);
  const meta = FIELD_EVENT_TYPES[kind];

  const switchKind = (k) => {
    setKind(k);
    if (!FIELD_EVENT_TYPES[k].units.includes(unit)) setUnit(FIELD_EVENT_TYPES[k].units[0]);
  };

  const payload = () => ({
    parcel_id: parcelId,
    ilot_id: entry?.ilot_id ?? ilotId ?? null,
    usage_type: kind,
    timing: { use_date: date },
    crop: { ...(entry?.crop || {}), crop_text: crop.trim(), variety: variety.trim() },
    cultivation: { quantity_value: qty === "" ? null : Number(String(qty).replace(",", ".")), quantity_unit: unit },
    treated_unit: { value: surface === "" ? null : Number(String(surface).replace(",", ".")), unit: "ha" },
    recommended_extra_data: { ...(entry?.recommended_extra_data || {}), observations: notes.trim() },
  });

  const validate = async (entryId) => {
    const res = await authFetch(`${API_BASE}/phyto-register/entries/${entryId}/validate`, { method: "POST" });
    if (res.ok) return true;
    const err = await res.json().catch(() => ({}));
    setErrors(err?.detail?.blocking_errors || [{ message: typeof err?.detail === "string" ? err.detail : "Validation impossible" }]);
    return false;
  };

  const submit = async () => {
    if (mode === "correct" && !draftId && reason.trim().length < 5) { toast.error("Motif de correction requis (5 caractères minimum)"); return; }
    setSaving(true); setErrors([]);
    try {
      let res;
      if (draftId) {
        res = await authFetch(`${API_BASE}/phyto-register/entries/${draftId}`, {
          method: "PUT", headers: { "Content-Type": "application/json" }, body: JSON.stringify(payload()),
        });
      } else if (mode === "correct") {
        res = await authFetch(`${API_BASE}/phyto-register/entries/${entry.entry_id}/correct`, {
          method: "POST", headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ correction_reason: reason.trim(), payload: payload() }),
        });
      } else {
        res = await authFetch(`${API_BASE}/phyto-register/entries${mode === "edit" ? `/${entry.entry_id}` : ""}`, {
          method: mode === "edit" ? "PUT" : "POST", headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload()),
        });
      }
      const saved = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(typeof saved.detail === "string" ? saved.detail : "Enregistrement impossible");
      const id = draftId || saved.entry_id || entry?.entry_id;
      if (await validate(id)) {
        toast.success(`${meta.label} enregistrée et verrouillée dans le registre`);
        onDone?.();
      } else {
        setDraftId(id);
        toast.warning("Enregistrée en brouillon : complétez les champs signalés");
        onDone?.({ keepOpen: true });
      }
    } catch (e) {
      toast.error(e.message);
    }
    setSaving(false);
  };

  const Icon = meta.icon;
  return (
    <ModalPortal>
      <div className="fixed inset-0 z-[2100] bg-black/50 flex items-end sm:items-center justify-center sm:p-4" data-testid="field-event-modal">
        <div className="bg-white w-full sm:max-w-md rounded-t-2xl sm:rounded-xl shadow-2xl max-h-[92dvh] flex flex-col overflow-hidden">
          <div className="px-4 py-3 flex items-center gap-2 text-white" style={{ background: meta.color }}>
            <Icon className="w-4 h-4" />
            <div className="flex-1 text-sm font-bold">
              {mode === "correct" ? `Corriger la ${meta.label.toLowerCase()}` : mode === "edit" ? `Modifier la ${meta.label.toLowerCase()}` : "Plantation / Récolte"}
            </div>
            <Button size="icon" variant="ghost" className="h-8 w-8 text-white hover:bg-white/20" onClick={onClose} data-testid="close-field-event"><X className="w-4 h-4" /></Button>
          </div>

          <div className="p-4 space-y-3 overflow-y-auto">
            {mode === "new" && (
              <div className="flex rounded-lg p-0.5 gap-0.5 bg-gray-100" role="tablist">
                {Object.entries(FIELD_EVENT_TYPES).map(([k, m]) => (
                  <button key={k} role="tab" aria-selected={kind === k} onClick={() => switchKind(k)} data-testid={`field-event-kind-${k}`}
                    className={`flex-1 h-8 rounded-md text-xs font-semibold transition ${kind === k ? "bg-white shadow-sm" : "text-gray-500"}`}
                    style={kind === k ? { color: m.color } : undefined}>{m.label}</button>
                ))}
              </div>
            )}

            <div className="grid grid-cols-2 gap-2.5">
              <div className="space-y-1">
                <Label htmlFor="fe-date" className="text-[11px]">Date de {kind === "PLANTATION" ? "plantation" : "récolte"} *</Label>
                <Input id="fe-date" type="date" value={date} max={today()} onChange={(e) => setDate(e.target.value)} className="h-9 text-sm" data-testid="field-event-date" />
              </div>
              <div className="space-y-1">
                <Label htmlFor="fe-crop" className="text-[11px]">Culture *</Label>
                <Input id="fe-crop" value={crop} onChange={(e) => setCrop(e.target.value)} className="h-9 text-sm" data-testid="field-event-crop" />
              </div>
              <div className="space-y-1 col-span-2">
                <Label htmlFor="fe-variety" className="text-[11px]">Variété *</Label>
                <Input id="fe-variety" list="fe-varieties" value={variety} onChange={(e) => setVariety(e.target.value)} placeholder="ex. R579" className="h-9 text-sm" data-testid="field-event-variety" />
                <datalist id="fe-varieties">{varieties.map((v) => <option key={v} value={v} />)}</datalist>
              </div>
              <div className="space-y-1">
                <Label htmlFor="fe-qty" className="text-[11px]">{meta.qty} *</Label>
                <Input id="fe-qty" inputMode="decimal" value={qty} onChange={(e) => setQty(e.target.value.replace(/[^0-9.,]/g, ""))} className="h-9 text-sm font-mono" data-testid="field-event-qty" />
              </div>
              <div className="space-y-1">
                <Label htmlFor="fe-unit" className="text-[11px]">Unité *</Label>
                <select id="fe-unit" value={unit} onChange={(e) => setUnit(e.target.value)} className="h-9 w-full rounded-md border border-input bg-white px-2 text-sm" data-testid="field-event-unit">
                  {meta.units.map((u) => <option key={u} value={u}>{u}</option>)}
                </select>
              </div>
              <div className="space-y-1 col-span-2">
                <Label htmlFor="fe-surface" className="text-[11px]">Surface concernée (ha)</Label>
                <Input id="fe-surface" inputMode="decimal" value={surface} onChange={(e) => setSurface(e.target.value.replace(/[^0-9.,]/g, ""))} className="h-9 text-sm font-mono" data-testid="field-event-surface" />
              </div>
              <div className="space-y-1 col-span-2">
                <Label htmlFor="fe-notes" className="text-[11px]">Observations</Label>
                <Textarea id="fe-notes" value={notes} onChange={(e) => setNotes(e.target.value)} rows={2} className="text-sm" data-testid="field-event-notes" />
              </div>
              {mode === "correct" && (
                <div className="space-y-1 col-span-2">
                  <Label htmlFor="fe-reason" className="text-[11px]">Motif de la correction *</Label>
                  <Input id="fe-reason" value={reason} onChange={(e) => setReason(e.target.value)} placeholder="ex. erreur de quantité" className="h-9 text-sm" data-testid="field-event-reason" />
                </div>
              )}
            </div>

            {errors.length > 0 && (
              <ul className="text-[11.5px] rounded-md p-2 space-y-0.5 bg-red-50 border border-red-200 text-red-800" data-testid="field-event-errors">
                {errors.map((e, i) => <li key={i}>• {e.message}</li>)}
              </ul>
            )}
          </div>

          <div className="border-t p-3 flex items-center justify-between gap-2 bg-gray-50" style={{ paddingBottom: "max(0.75rem, env(safe-area-inset-bottom))" }}>
            <p className="text-[10.5px] text-gray-500 flex items-center gap-1"><Lock className="w-3 h-3" />Verrouillée après enregistrement</p>
            <Button size="sm" onClick={submit} disabled={saving} className="h-9 text-xs text-white" style={{ background: meta.color }} data-testid="field-event-save">
              {saving && <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" />}Enregistrer et valider
            </Button>
          </div>
        </div>
      </div>
    </ModalPortal>
  );
}
